import React from 'react';
import { usePullToRefresh, UsePullToRefreshOptions, PullDirection } from '../../hooks/usePullToRefresh';

export interface PullToRefreshProps extends UsePullToRefreshOptions {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    id?: string;
}

/**
 * Indicador visual circular de 24px en #2563EB estilo iOS (arriba y abajo).
 */
export const PullToRefreshIndicator: React.FC<{
    pullDistance: number;
    pullDirection: PullDirection;
    isRefreshing: boolean;
    threshold?: number;
}> = ({ pullDistance, pullDirection, isRefreshing, threshold = 70 }) => {
    if (!pullDirection && !isRefreshing) return null;
    if (pullDistance <= 0 && !isRefreshing) return null;

    const isDown = pullDirection === 'down' || (!pullDirection && isRefreshing);
    const rotation = (pullDistance / threshold) * 360;

    return (
        <div
            className={`absolute left-0 right-0 z-30 flex items-center justify-center pointer-events-none transition-opacity duration-150 ${
                isDown ? 'top-2' : 'bottom-20'
            }`}
            style={{
                opacity: Math.min(1, pullDistance / (threshold * 0.5)),
                transform: isDown
                    ? `translateY(${Math.min(pullDistance * 0.45, 36)}px)`
                    : `translateY(-${Math.min(pullDistance * 0.45, 36)}px)`
            }}
        >
            <div
                className="size-8 rounded-full bg-white dark:bg-slate-800 shadow-md border border-slate-200/90 dark:border-slate-700 flex items-center justify-center"
                style={{
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                }}
            >
                <div
                    style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: '2.5px solid rgba(37, 99, 235, 0.2)',
                        borderTopColor: '#2563EB',
                        transform: isRefreshing ? undefined : `rotate(${rotation}deg)`
                    }}
                    className={isRefreshing ? 'animate-spin' : ''}
                />
            </div>
        </div>
    );
};

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
    children,
    onRefresh,
    disabled = false,
    className = '',
    style = {},
    id,
    threshold = 70,
    maxPull = 100
}) => {
    const {
        containerRef,
        pullDistance,
        pullDirection,
        isRefreshing,
        contentStyle
    } = usePullToRefresh({
        onRefresh,
        disabled,
        threshold,
        maxPull
    });

    return (
        <div
            ref={containerRef}
            id={id}
            className={`relative overflow-y-auto ${className}`}
            style={{
                WebkitOverflowScrolling: 'touch',
                ...style
            }}
        >
            {/* Indicador de actualización circular de 24px */}
            <PullToRefreshIndicator
                pullDistance={pullDistance}
                pullDirection={pullDirection}
                isRefreshing={isRefreshing}
                threshold={threshold}
            />

            {/* Contenido con desplazamiento y resistencia */}
            <div style={contentStyle}>
                {children}
            </div>
        </div>
    );
};

export default PullToRefresh;
